import { RadarChartOutlined, UserOutlined } from '@ant-design/icons';
import React from 'react';
import { Navigate } from 'react-router-dom';

// 路由级代码分割 — 使用 React.lazy 延迟加载页面组件
const Login = React.lazy(() => import('../pages/Login'));
const MarketingOperator = React.lazy(() => import('../pages/dashboard/MarketingOperator'));
const LeadHunter = React.lazy(() => import('../pages/lead/LeadHunter'));
const CommunityManager = React.lazy(() => import('../pages/community/CommunityManager'));
const ContentScout = React.lazy(() => import('../pages/content/ContentScout'));
const ContentScoutCompetitorVideos = React.lazy(() => import('../pages/content/ContentScoutCompetitorVideos'));
const ProductSpecialist = React.lazy(() => import('../pages/product-selection/ProductSpecialist'));
const ProductSpecialistProductDetail = React.lazy(() => import('../pages/product-selection/ProductSpecialistProductDetail'));
const CreativeWriter = React.lazy(() => import('../pages/creative/CreativeWriter'));
const PublishingMonitor = React.lazy(() => import('../pages/publishing/PublishingMonitor'));
const PublishingMonitorVideos = React.lazy(() => import('../pages/publishing/PublishingMonitorVideos'));
const PublishingMonitorPublishedVideos = React.lazy(() => import('../pages/publishing/PublishingMonitorPublishedVideos'));
const CreativeWriterVideos = React.lazy(() => import('../pages/creative/CreativeWriterVideos'));
const CreativeWriterFavorites = React.lazy(() => import('../pages/creative/CreativeWriterFavorites'));
const MyProducts = React.lazy(() => import('../pages/dashboard/MyProducts'));
const Settings = React.lazy(() => import('../pages/settings/Settings'));
const AccountBilling = React.lazy(() => import('../pages/settings/AccountBilling'));
const MyDevices = React.lazy(() => import('../pages/settings/MyDevices'));
const DevTools = React.lazy(() => import('../pages/dev/DevTools'));
const DevToolsBrowser = React.lazy(() => import('../pages/dev/DevToolsBrowser'));
const DevToolsDouyinIm = React.lazy(() => import('../pages/dev/DevToolsDouyinIm'));
const DevToolsDouyinCreatorPublish = React.lazy(() => import('../pages/dev/DevToolsDouyinCreatorPublish'));
const DevToolsAppUpdate = React.lazy(() => import('../pages/dev/DevToolsAppUpdate'));
const DevToolsEcommerceAccountSessions = React.lazy(() => import('../pages/dev/DevToolsEcommerceAccountSessions'));
const DevToolsShopOverview = React.lazy(() => import('../pages/dev/DevToolsShopOverview'));
const DevToolsShowcaseOverview = React.lazy(() => import('../pages/dev/DevToolsShowcaseOverview'));
const DevToolsComputeRecords = React.lazy(() => import('../pages/dev/DevToolsComputeRecords'));
const BuyingProductSelection = React.lazy(() => import('../pages/product-selection/BuyingProductSelection'));
const BuyingProductSelectionLibrary = React.lazy(() => import('../pages/product-selection/BuyingProductSelectionLibrary'));
const BuyingProductSelectionDetail = React.lazy(() => import('../pages/product-selection/BuyingProductSelectionDetail'));
const Notifications = React.lazy(() => import('../pages/notifications/Notifications'));

export interface RouteConfig {
  path: string;
  name: string;
  icon?: React.ReactNode;
  element: React.ReactNode;
  hideInMenu?: boolean; // Some routes like 404 or login might not be in the sidebar
}

export const routes: RouteConfig[] = [
  {
    path: '/login',
    name: '登录',
    element: <Login />,
    hideInMenu: true,
  },
  {
    path: '/marketing-operator',
    name: '数据总览',
    icon: <UserOutlined />,
    element: <MarketingOperator />,
  },
  {
    path: '/marketing-operator/products',
    name: '我的货品',
    element: <MyProducts />,
    hideInMenu: true,
  },
  {
    path: '/settings',
    name: '设置',
    element: <Navigate to="/settings/accounts" replace />,
    hideInMenu: true,
  },
  {
    path: '/notifications',
    name: '通知',
    element: <Notifications />,
    hideInMenu: true,
  },
  {
    path: '/settings/devices',
    name: '我的设备',
    element: <MyDevices />,
    hideInMenu: true,
  },
  {
    path: '/settings/billing',
    name: '账户与计费',
    element: <AccountBilling />,
    hideInMenu: true,
  },
  {
    path: '/settings/*',
    name: '设置',
    element: <Settings />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools',
    name: '开发调试中心',
    element: <DevTools />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/browser',
    name: '浏览器调试设置',
    element: <DevToolsBrowser />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/douyin-im',
    name: '抖音私信调试',
    element: <DevToolsDouyinIm />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/douyin-creator-publish',
    name: '创作者平台调试',
    element: <DevToolsDouyinCreatorPublish />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/ecommerce-account-sessions',
    name: '账号登录态调试',
    element: <DevToolsEcommerceAccountSessions />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/shop-overview',
    name: '店铺概况调试',
    element: <DevToolsShopOverview />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/showcase-overview',
    name: '橱窗概况调试',
    element: <DevToolsShowcaseOverview />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/compute-records',
    name: '算力流水记录',
    element: <DevToolsComputeRecords />,
    hideInMenu: true,
  },
  {
    path: '/product-selection/buying',
    name: '选品专员',
    element: <BuyingProductSelection />,
    hideInMenu: true,
  },
  {
    path: '/product-selection/buying/selection-library',
    name: '商品测品选品库',
    element: <BuyingProductSelectionLibrary />,
    hideInMenu: true,
  },
  {
    path: '/product-selection/buying/selection-library/:selectionId',
    name: '商品测品选品库详情',
    element: <BuyingProductSelectionDetail />,
    hideInMenu: true,
  },
  {
    path: '/dev-tools/app-update',
    name: '应用更新调试',
    element: <DevToolsAppUpdate />,
    hideInMenu: true,
  },
  {
    path: '/devices',
    name: '旧版设备入口',
    element: <MyDevices />,
    hideInMenu: true,
  },
  {
    path: '/lead-hunter',
    name: '获客助手',
    icon: <RadarChartOutlined />,
    element: <LeadHunter />,
  },
  {
    path: '/community-manager',
    name: '社群运营官',
    icon: <RadarChartOutlined />,
    element: <CommunityManager />,
  },
  {
    path: '/content-scout',
    name: '素材情报员',
    icon: <RadarChartOutlined />,
    element: <ContentScout />,
  },
  {
    path: '/content-scout/competitor-videos',
    name: '竞对账号爆款视频',
    element: <ContentScoutCompetitorVideos />,
    hideInMenu: true,
  },
  {
    path: '/product-specialist',
    name: '选品专员',
    icon: <RadarChartOutlined />,
    element: <ProductSpecialist />,
  },
  {
    path: '/product-specialist/products/:productId',
    name: '选品商品详情',
    element: <ProductSpecialistProductDetail />,
    hideInMenu: true,
  },
  {
    path: '/creative-writer',
    name: '创意编剧',
    icon: <RadarChartOutlined />,
    element: <CreativeWriter />,
  },
  {
    path: '/publishing-monitor',
    name: '投放盯盘手',
    icon: <RadarChartOutlined />,
    element: <PublishingMonitor />,
  },
  {
    path: '/publishing-monitor/videos',
    name: '投放盯盘手-生成视频',
    element: <PublishingMonitorVideos />,
    hideInMenu: true,
  },
  {
    path: '/publishing-monitor/published-videos',
    name: '投放盯盘手-全部投放视频',
    element: <PublishingMonitorPublishedVideos />,
    hideInMenu: true,
  },
  {
    path: '/creative-writer/videos',
    name: '创意编剧-视频资产',
    element: <CreativeWriterVideos />,
    hideInMenu: true,
  },
  {
    path: '/creative-writer/favorites',
    name: '创意编剧-模板收藏',
    element: <CreativeWriterFavorites />,
    hideInMenu: true,
  },
];
